import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ChatMessage } from '@/utils/chat-service';
import { v4 as uuidv4 } from '@/utils/uuid';

export type ChatMode = 'add' | 'ask';
export type MobileChatViewState = 'initial' | 'add-chat' | 'ask-chat';

interface ChatPersistState {
  // Desktop Track page state
  messages: ChatMessage[];
  selectedSlotDate: Date | null;
  showChat: boolean;
  inputValue: string;
  entrySaved: boolean;
  lastSavedEntryId: string | null;
  lastSavedEntryDate: string | null;
  // Mobile Chat page state
  mobileMode: ChatMode;
  mobileViewState: MobileChatViewState;
}

// Format date for display in welcome message
const formatEntryDateMessage = (date: Date | null | undefined): string => {
  if (!date) return "What would you like to track now?";

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const time = `${hours}:${minutes}`;

  if (isToday) {
    if (date.getHours() !== now.getHours()) {
      return `What would you like to track for ${time}?`;
    }
    return "What would you like to track now?";
  }

  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();

  return `What would you like to track for ${time} on ${day} ${month} ${year}?`;
};

const createInitialMessage = (entryDate?: Date | null): ChatMessage => ({
  id: uuidv4(),
  role: 'assistant',
  content: formatEntryDateMessage(entryDate),
  timestamp: new Date()
});

interface ChatPersistContextType {
  state: ChatPersistState;
  setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
  setSelectedSlotDate: (date: Date | null) => void;
  setShowChat: (show: boolean) => void;
  setInputValue: (value: string) => void;
  setEntrySaved: (saved: boolean) => void;
  setLastSavedEntryId: (id: string | null) => void;
  setLastSavedEntryDate: (date: string | null) => void;
  resetChat: (entryDate?: Date | null) => void;
  hasUnsavedContent: () => boolean;
  // Mobile Chat page functions
  setMobileMode: (mode: ChatMode) => void;
  setMobileViewState: (viewState: MobileChatViewState) => void;
  resetMobileChat: () => void;
}

const ChatPersistContext = createContext<ChatPersistContextType | undefined>(undefined);

export const ChatPersistProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<ChatPersistState>({
    messages: [createInitialMessage()],
    selectedSlotDate: null,
    showChat: false,
    inputValue: '',
    entrySaved: false,
    lastSavedEntryId: null,
    lastSavedEntryDate: null,
    // Mobile Chat page initial state
    mobileMode: 'add',
    mobileViewState: 'initial',
  });

  const setMessages = useCallback((messagesOrUpdater: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
    setState(prev => ({
      ...prev,
      messages: typeof messagesOrUpdater === 'function'
        ? messagesOrUpdater(prev.messages)
        : messagesOrUpdater
    }));
  }, []);

  const setSelectedSlotDate = useCallback((date: Date | null) => {
    setState(prev => {
      // If the date changed, update the welcome message but preserve other messages
      if (prev.selectedSlotDate?.getTime() !== date?.getTime()) {
        // Only update if we haven't started a conversation yet (just welcome message)
        const hasOnlyWelcomeMessage = prev.messages.length === 1 && prev.messages[0].role === 'assistant';
        if (hasOnlyWelcomeMessage) {
          return {
            ...prev,
            selectedSlotDate: date,
            messages: [createInitialMessage(date)]
          };
        }
      }
      return {
        ...prev,
        selectedSlotDate: date
      };
    });
  }, []);

  const setShowChat = useCallback((show: boolean) => {
    setState(prev => ({
      ...prev,
      showChat: show
    }));
  }, []);

  const setInputValue = useCallback((value: string) => {
    setState(prev => ({
      ...prev,
      inputValue: value
    }));
  }, []);

  const setEntrySaved = useCallback((saved: boolean) => {
    setState(prev => ({
      ...prev,
      entrySaved: saved
    }));
  }, []);

  const setLastSavedEntryId = useCallback((id: string | null) => {
    setState(prev => ({
      ...prev,
      lastSavedEntryId: id
    }));
  }, []);

  const setLastSavedEntryDate = useCallback((date: string | null) => {
    setState(prev => ({
      ...prev,
      lastSavedEntryDate: date
    }));
  }, []);

  const resetChat = useCallback((entryDate?: Date | null) => {
    setState(prev => ({
      ...prev,
      messages: [createInitialMessage(entryDate)],
      selectedSlotDate: entryDate || null,
      showChat: false,
      inputValue: '',
      entrySaved: false,
      lastSavedEntryId: null,
      lastSavedEntryDate: null,
    }));
  }, []);

  const hasUnsavedContent = useCallback(() => {
    // Check if there's any user content beyond the initial assistant message
    return state.messages.length > 1 || state.inputValue.trim().length > 0;
  }, [state.messages, state.inputValue]);

  // Mobile Chat page functions
  const setMobileMode = useCallback((mode: ChatMode) => {
    setState(prev => ({
      ...prev,
      mobileMode: mode
    }));
  }, []);

  const setMobileViewState = useCallback((viewState: MobileChatViewState) => {
    setState(prev => ({
      ...prev,
      mobileViewState: viewState
    }));
  }, []);

  const resetMobileChat = useCallback(() => {
    setState(prev => ({
      ...prev,
      messages: [createInitialMessage()],
      inputValue: '',
      entrySaved: false,
      lastSavedEntryId: null,
      lastSavedEntryDate: null,
      mobileViewState: 'initial',
    }));
  }, []);

  return (
    <ChatPersistContext.Provider value={{
      state,
      setMessages,
      setSelectedSlotDate,
      setShowChat,
      setInputValue,
      setEntrySaved,
      setLastSavedEntryId,
      setLastSavedEntryDate,
      resetChat,
      hasUnsavedContent,
      setMobileMode,
      setMobileViewState,
      resetMobileChat
    }}>
      {children}
    </ChatPersistContext.Provider>
  );
};

export const useChatPersist = () => {
  const context = useContext(ChatPersistContext);
  if (context === undefined) {
    throw new Error('useChatPersist must be used within a ChatPersistProvider');
  }
  return context;
};
